/**
 * Upload TFL API Module
 *
 * This module provides API functions for uploading TFL files.
 * Uses the customInstance function from auth feature's axios-config for
 * enhanced error handling, retry logic, and cancellation support.
 */

import { BodyType, customInstance } from '@/features/auth/axios-config'
import { UpdateDocument } from '@/features/document/types.ts'
import { BookmarkResponse } from '@/features/view-tfl-uploaded/types.ts'
import {
  PaginatedResponseResponseDocumentListItem,
  RequestFilters,
  RequestFulltextSearchDocument,
  SuccessResponseListAISearchResultResponse,
  SuccessResponseListResponseDocumentRetrieval,
  SuccessResponseListResponseDocumentVersion,
} from '@/schema'
import { mergeTablePaginationParams } from '@/utils'

export type SecondParameter<T extends (...args: never) => unknown> =
  Parameters<T>[1]

export interface GetResignUrlResponse {
  resignUrl: string
}

export interface UploadFileResponse {
  success: boolean
  message?: string
}

const API_ENDPOINT_DOCUMENTS = `/document/v1/documents`
const buildApiById = (id: string) => `${API_ENDPOINT_DOCUMENTS}/${id}`
const buildApiByVersionId = (documentId: string, versionId: string) =>
  `${buildApiById(documentId)}/versions/${versionId}`

export const getDocumentList = async (
  data: Partial<RequestFilters> = {}
): Promise<PaginatedResponseResponseDocumentListItem> => {
  const requestBody = mergeTablePaginationParams(data)

  return customInstance<PaginatedResponseResponseDocumentListItem>({
    method: 'post',
    url: `${API_ENDPOINT_DOCUMENTS}/list`,
    data: requestBody,
  })
}

export const getDocumentRetrieval = async (
  documentId: string
): Promise<SuccessResponseListResponseDocumentRetrieval> => {
  return customInstance<SuccessResponseListResponseDocumentRetrieval>({
    method: 'get',
    url: `${API_ENDPOINT_DOCUMENTS}/${documentId}`,
  })
}

export const deleteDocument = async (documentId: string): Promise<void> => {
  return customInstance<void>({
    method: 'delete',
    url: `${API_ENDPOINT_DOCUMENTS}/${documentId}`,
  })
}

export const updateDocument = async (
  documentId: string,
  requestBody: UpdateDocument
): Promise<SuccessResponseListResponseDocumentRetrieval> => {
  return customInstance<SuccessResponseListResponseDocumentRetrieval>({
    method: 'put',
    url: `${API_ENDPOINT_DOCUMENTS}/${documentId}`,
    data: requestBody,
  })
}

export const getVersionList = async (
  documentId: string
): Promise<SuccessResponseListResponseDocumentVersion> => {
  return customInstance<SuccessResponseListResponseDocumentVersion>({
    method: 'get',
    url: `${buildApiById(documentId)}/versions`,
  })
}

export const getDocumentVersion = async (
  documentId: string,
  versionId: string
): Promise<SuccessResponseListResponseDocumentRetrieval> => {
  return customInstance<SuccessResponseListResponseDocumentRetrieval>({
    method: 'get',
    url: buildApiByVersionId(documentId, versionId),
  })
}

export const addBookmark = async (
  documentId: string
): Promise<BookmarkResponse> => {
  return customInstance<BookmarkResponse>({
    method: 'post',
    url: `${API_ENDPOINT_DOCUMENTS}/${documentId}/bookmark`,
  })
}

export const removeBookmark = async (
  documentId: string
): Promise<BookmarkResponse> => {
  return customInstance<BookmarkResponse>({
    method: 'delete',
    url: `${API_ENDPOINT_DOCUMENTS}/${documentId}/bookmark`,
  })
}

/**
 * Search documents
 * @summary Search documents
 */
export const searchDocuments = (
  requestFulltextSearchDocument: BodyType<RequestFulltextSearchDocument>,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SuccessResponseListAISearchResultResponse>(
    {
      url: `${API_ENDPOINT_DOCUMENTS}/search`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: requestFulltextSearchDocument,
      signal,
    },
    options
  )
}
